import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Header from "./Header";
import SelectNextHopModal from "./SelectNextHopModal";
import Tooltip from "../../../Components/common/Tooltip/Tooltip";
import Spinner from "../../../Components/common/Spinner/Spinner";
import FileUpload from "../../../Components/common/FileUpload/FileUpload";
import Field from "../../Settings/Content/Components/Field";

import {
  RequestGetCertificateFile,
  RequestGetGeneralSettings,
  RequestGetKeyFile,
  RequestSetCertificateFile,
  RequestSetGeneralSettings,
  RequestSetKeyFile,
  
} from "../../../store/actions/mailGatewayActions";
import { addNotification } from "../../../store/actions/notifierActions";

const MIN_MAX_VALUES = {
  timeout_file_analysis: [6, 12],
  files_to_analyse: [1, 6],
  urls_to_check: [1, 20],
};

const isValidHostname = (hostname) => {
  if (typeof hostname !== "string") return false;

  // Genel uzunluk kontrolü
  if (hostname.length === 0 || hostname.length > 253) return false;

  // Etiketleri ayır
  const labels = hostname.split(".");

  for (const label of labels) {
    // Etiketler boş olamaz, 63 karakteri geçemez
    if (label.length === 0 || label.length > 63) return false;

    // Etiket sadece harf, rakam, tire içerebilir ve tire ile başlayıp bitemez
    if (!/^[a-zA-Z0-9]([-a-zA-Z0-9]*[a-zA-Z0-9])?$/.test(label)) {
      return false;
    }
  }

  return true;
};

const GeneralSettingsForm = ({ selectedProduct, selectedHost }) => {
  const dispatch = useDispatch();

  const [settings, setSettings] = useState({
    timeout_file_analysis: "",
    files_to_analyse: "",
    urls_to_check: "",
    myhostname: "",
    bypass_unexpected: "",
  });
  
  const [certificateFile, setCertificateFile] = useState(null);
  const [certificateFileError, setCertificateFileError] = useState("");
  const [keyFile, setKeyFile] = useState(null);
  const [keyFileError, setKeyFileError] = useState("");
  const [showModal, setShowModal] = useState(false);

  const generalSettingsFromDB = useSelector((state) => state.mailGateway.generalSettings);
  const getSettingsLoading = useSelector((state) => state.mailGateway.getGeneralSettingsLoading);
  const setSettingsLoading = useSelector((state) => state.mailGateway.setGeneralSettingsLoading);

  const currentCertificateFile = useSelector((state) => state.mailGateway.certificateFile);
  const isFileUploaded = useSelector((state) => state.mailGateway.certificateFileUploaded);
  const currentKeyFile = useSelector((state) => state.mailGateway.keyFile);
  const isFileUploadedKey = useSelector((state) => state.mailGateway.keyFileUploaded);

  useEffect(() => {
    dispatch(RequestGetGeneralSettings(selectedHost));
    dispatch(RequestGetCertificateFile(selectedHost));
    dispatch(RequestGetKeyFile(selectedHost));
  }, [dispatch, selectedHost]);

  useEffect(() => {
    if (generalSettingsFromDB && selectedHost) {
      setSettings({
        myhostname: generalSettingsFromDB?.myhostname || "",
        timeout_file_analysis: Number(generalSettingsFromDB?.timeout_file_analysis || ""),
        files_to_analyse: Number(generalSettingsFromDB?.files_to_analyse || ""),
        urls_to_check: Number(generalSettingsFromDB?.urls_to_check || ""),
        bypass_unexpected: generalSettingsFromDB?.bypass_unexpected || "",
      });
    }
  }, [generalSettingsFromDB, selectedHost]);

  useEffect(() => {
    if (isFileUploaded) {
      setCertificateFile(null);
    }
  }, [isFileUploaded]);

  useEffect(() => {
    if (isFileUploadedKey) {
      setKeyFile(null);
    }
  }, [isFileUploadedKey]);

  const handleSelectNextHop = () => {
    setShowModal(true);
  };

  const handleDownloadCertificateFile = () => {
    const byteCharacters = atob(currentCertificateFile.filedata_b64);
    const byteNumbers = new Array(byteCharacters.length)
      .fill()
      .map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);

    const blob = new Blob([byteArray], {
      type: "application/x-x509-ca-cert", // .crt için mime type
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = currentCertificateFile.filename;
    document.body.appendChild(a);
    a.click();

    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleDownloadKeyFile = () => {
    const byteCharacters = atob(currentKeyFile.filedata_b64);
    const byteNumbers = new Array(byteCharacters.length)
      .fill()
      .map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);

    const blob = new Blob([byteArray], {
      type: "application/x-x509-ca-key", // .crt için mime type
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = currentKeyFile.filename;
    document.body.appendChild(a);
    a.click();

    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleCertificateFileChange = (file) => {
    setCertificateFile(file);
  };

  const handleCertificateFileUpload = () => {
    if (!selectedHost || !certificateFile) return;
    dispatch(RequestSetCertificateFile({ host: selectedHost, file: certificateFile }));
  };

  const handleKeyFileChange = (file) => {
    setKeyFile(file);
  };

  const handleKeyFileUpload = () => {
    if (!selectedHost || !keyFile) return;
    dispatch(RequestSetKeyFile({ host: selectedHost, file: keyFile }));
  };

  const inputChangeHandler = (e) => {
    const name = e.target.name;
    let value = e.target.value;

    if (name !== "bypass_unexpected") {

      if (name === "myhostname") {
        value = value.trim();
      } else {
        const limits = MIN_MAX_VALUES[name];
        const [min, max] = limits;

        if (value < min) {
          value = min;
        } else if (value > max) {
          value = max;
        }
        value = Number(value);
      }
    }

    setSettings((prevSettings) => ({
      ...prevSettings,
      [name]: value,
    }));
  };

  const checkSaveButtonDisabled = () => {
    const keys = Object.keys(settings);
    for (const key of keys) {
      if (key === "myhostname" && settings[key] === generalSettingsFromDB[key]) continue;
      else if (settings[key] === Number(generalSettingsFromDB[key])) continue;
      else if (settings[key] !== generalSettingsFromDB[key]) return false;
    }
    return true;
  };

  const saveHandler = () => {
    if (getSettingsLoading || setSettingsLoading) return;
    if (isValidHostname(settings.myhostname)) {
      dispatch(RequestSetGeneralSettings({ host: selectedHost, reqBody: settings }));
    } else {
      dispatch(
        addNotification({
          message: "Enter a valid hostname",
          options: { variant: "error" },
          time: 3000,
        }),
      );
    }
  };

  return (
    <>
      <Header selectedProduct={selectedProduct} selectedHost={selectedHost} />
      <div className="products-body-form">
        <div className="products-body-form__line">
          <div className="mailing__content__security m-b-15">
            <p className="light m-b-10 mailing__content__security__header">
              Send Test Email
              <Tooltip text="Click the button below to select a next hop that you want to send a test email" showFlowGrow={true} />
            </p>
            <button
              style={{ marginBottom: "4px" }}
              // className="primary-button"
              className="products-body-form__file-button"
              onClick={handleSelectNextHop}>
              Select Next Hop
            </button>
          </div>
        </div>

        

        <div className="products-body-form__file">
          <p className="light m-b-10 mailing__content__security__header">
            Certificate File
            <Tooltip text="Upload a crt file (max size is 20 Kb)" showFlowGrow={true} />
          </p>
          <div className="products-body-form__file-container">
            <div className="products-body-form__file-current">
              <p>
                {currentCertificateFile?.filename
                  ? `${currentCertificateFile.filename}`
                  : "No file found"}
              </p>
              {/* <button
                className="products-body-form__file-button"
                disabled={!currentCertificateFile?.filename}>
                Preview
              </button> */}
              <button
                className="products-body-form__file-button"
                disabled={!currentCertificateFile?.filename}
                onClick={handleDownloadCertificateFile}>
                Download
              </button>
            </div>
            <div className="products-body-form__file-upload">
              <FileUpload
                file={certificateFile}
                setFile={handleCertificateFileChange}
                setFileError={setCertificateFileError}
                allowedExtensions={["crt"]}
                errorMessage="Please select a .crt file"
                digitsShowed={3}
                maxSize={20 * 1024} // 20 KB
                styles={{
                  formWidth: "220px",
                  formHeight: "84px",
                  iconContainerWidth: "28px",
                  iconContainerHeight: "28px",
                  iconWidth: "20px",
                  iconHeight: "20px",
                  fontSize: "11.5px",
                  infoTextPadding: "0px 38px",
                }}
                mid="file-upload-1"
              />
              <div>
                {certificateFileError ? (
                  <p>{certificateFileError}</p>
                ) : (
                  <button
                    className="products-body-form__file-button"
                    onClick={handleCertificateFileUpload}
                    disabled={!certificateFile}>
                    Upload
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="products-body-form__file">
          <p className="light m-b-10 mailing__content__security__header">
            Key File
            <Tooltip text="Upload a key file (max size is 20 Kb)" showFlowGrow={true} />
          </p>
          <div className="products-body-form__file-container">
            <div className="products-body-form__file-current">
              <p>
                {currentKeyFile?.filename
                  ? `${currentKeyFile.filename}`
                  : "No file found"}
              </p>
              {/* <button
                className="products-body-form__file-button"
                disabled={!currentCertificateFile?.filename}>
                Preview
              </button> */}
              <button
                className="products-body-form__file-button"
                disabled={!currentKeyFile?.filename}
                onClick={handleDownloadKeyFile}>
                Download
              </button>
            </div>
            <div className="products-body-form__file-upload">
              <FileUpload
                file={keyFile}
                setFile={handleKeyFileChange}
                setFileError={setKeyFileError}
                allowedExtensions={["key"]}
                errorMessage="Please select a .key file"
                digitsShowed={3}
                maxSize={20 * 1024} // 20 KB
                styles={{
                  formWidth: "220px",
                  formHeight: "84px",
                  iconContainerWidth: "28px",
                  iconContainerHeight: "28px",
                  iconWidth: "20px",
                  iconHeight: "20px",
                  fontSize: "11.5px",
                  infoTextPadding: "0px 38px",
                }}
                mid="file-upload-2"
              />
              <div>
                {keyFileError ? (
                  <p>{keyFileError}</p>
                ) : (
                  <button
                    className="products-body-form__file-button"
                    onClick={handleKeyFileUpload}
                    disabled={!keyFile}>
                    Upload
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="products-body-form__line">
          <div className="mailing__content__security m-b-20">
            <p className="light m-b-10 mailing__content__security__header">
              Bypass Unexcpected Encoding
              <Tooltip text="Bypass unknown or unexpected encoded emails" showFlowGrow={true} />
            </p>
            <div className="mailing__content__security__labels">
              <label className="align-items">
                  <input
                    className="mailing__content__security__input"
                    type="radio"
                    value="1"
                    name="bypass_unexpected"
                    checked={settings.bypass_unexpected === "1"}
                    onChange={inputChangeHandler}
                  />
                  <p className="margin-left-12">Enabled</p>
                </label>
                <label className="align-items">
                  <input
                    className="mailing__content__security__input"
                    type="radio"
                    value="0"
                    name="bypass_unexpected"
                    checked={settings.bypass_unexpected === "0"}
                    onChange={inputChangeHandler}
                  />
                  <p className="margin-left-12">Disabled</p>
                </label>
              </div>
          </div>
        </div>

        <div className="products-body-form__line">
          <Field
            tooltipText="Analysis duration in minutes before timeout (6-12)"
            label="Timeout of File Analysis (mins)"
            width="220px"
            name="timeout_file_analysis"
            value={settings.timeout_file_analysis}
            onChange={inputChangeHandler}
            type="number"
            showFlowGrow={true}
          />
        </div>

        <div className="products-body-form__line">
          <div className="p-r-20">
            <Field
              tooltipText="Number of files to analyse (1-6)"
              label="Files to Analyse"
              width="220px"
              name="files_to_analyse"
              value={settings.files_to_analyse}
              onChange={inputChangeHandler}
              type="number"
            />
          </div>
          <Field
            tooltipText="Number of urls to check (1-20)"
            label="Urls to Check"
            width="220px"
            name="urls_to_check"
            value={settings.urls_to_check}
            onChange={inputChangeHandler}
            type="number"
          />
        </div>
        <br/>
        <div className="products-body-form__line">
          <div className="mailing__content__security m-b-20">
            <div className="p-r-20">
              <Field
                tooltipText={`Must not be empty.
Must not exceed 253 characters in total.
Each label (separated by .) must:
  - Be between 1 and 63 characters long.
  - Only contain letters (A–Z, a–z), digits (0–9), and hyphens (-).
  - Not start or end with a hyphen.
Must not contain spaces or special characters (e.g., @, #, _, /).
Must not include protocols like http:// or https://.`}
                label="MTA Hostname"
                width="220px"
                name="myhostname"
                value={settings.myhostname}
                onChange={inputChangeHandler}
                type="text"
                showFlowGrow={true}
              />
            </div>
          </div>
        </div>

        <button
          type="button"
          className="save-button primary-button"
          onClick={saveHandler}
          disabled={checkSaveButtonDisabled()}>
          {getSettingsLoading || setSettingsLoading ? (
            <Spinner width={12} height={12} />
          ) : (
            "Save Settings"
          )}
        </button>
        {showModal ? (
          <SelectNextHopModal selectedHost={selectedHost} show={showModal} setShow={setShowModal} />
        ) : null}
      </div>
    </>
  );
};

export default GeneralSettingsForm;
